import {
	btn,
	column,
	comp,
	DataSourceStore,
	datasourcestore,
	Format,
	store,
	t,
	table,
	Table,
	tbar,
	TextField,
	textfield,
	Window
} from "@intermesh/goui";
import {entities, LinkConfig, modules, searchDS} from "@intermesh/groupoffice-core";
import {cardDS} from "./Index.js";

export class SearchWindow extends Window {
	private resultTable: Table<DataSourceStore>;

	private readonly boardId: string;
	private readonly columnId: string;

	constructor(boardId: string, columnId: string) {
		super();

		this.boardId = boardId;
		this.columnId = columnId;

		this.title = t("Add existing item");

		this.height = 500;
		this.width = 800;

		this.resizable = true;

		const supportedEntities: LinkConfig[] = [];

		if (modules.isAvailable("community", "tasks")) {
			supportedEntities.push(entities.getLinkConfig("Task")!);
		}

		if (modules.isAvailable("business", "support")) {
			supportedEntities.push(entities.getLinkConfig("SupportTicket")!);
		}

		if (modules.isAvailable("business", "projects3")) {
			supportedEntities.push(entities.getLinkConfig("Project3")!);
		}

		this.items.add(
			comp({cls: "hbox fit", flex: 1},
				comp({cls: "vbox border-right"},
					comp({width: 180, cls: "fit scroll", flex: 1},
						table({
							headers: false,
							store: store({
								data: supportedEntities
							}),
							columns: [
								column({
									id: "iconCls",
									sortable: false,
									width: 48,
									htmlEncode: false,
									renderer: (iconCls, record, td, table1, storeIndex, column1) => {
										return `<div class="icon ${iconCls}"</div>`;
									}
								}),
								column({
									id: "title"
								})
							],
							rowSelectionConfig: {
								clickToAdd: true,
								multiSelect: true,
								listeners: {
									selectionchange: ({selected}) => {
										this.resultTable.store.setFilter("entityType", {
											entities: selected.map((r) => {
												return {name: r.record.entity, filter: r.record.filter};
											})
										});

										this.resultTable.store.load();
									}
								}
							},
							listeners: {
								rowclick: ({target, row, storeIndex}) => {
									const selected = target.rowSelection!.getSelected().map(row => row.record);
									const r = target.store.get(storeIndex)!;

									if (selected.find(s => s.entity == r.entity)) {
										target.rowSelection!.selectIndex(storeIndex);
									}
								}
							}
						})
					)
				),
				comp({flex: 1, cls: "vbox"},
					tbar({cls: "border-bottom"},
						textfield({
							flex: 1,
							icon: "search",
							buttons: [
								btn({
									icon: "clear",
									handler: btn => {
										const tf = btn.findAncestorByType(TextField)!
										tf.reset()
										tf.focus();
									}
								})
							]
						})
							.on("input", ({value}) => {
								this.resultTable.store.setFilter("search", {text: value});
								void this.resultTable.store.load();
							}, {buffer: 300})
							.on("focus", ({target}) => {
								target.select();
							})
							.on("render", ({target}) => {
								target.el.addEventListener("keydown", (ev) => {
									if (ev.key == "ArrowDown") {
										this.resultTable.focus();
									}
								})
							})
					),
					comp({
							flex: 1,
							cls: "scroll bg-lowest"
						},
						this.resultTable = table({
							scrollLoad: true,
							headers: false,
							style: {width: "100%"},
							rowSelectionConfig: {multiSelect: true},
							store: datasourcestore({
								dataSource: searchDS,
								sort: [{isAscending: false, property: "modifiedAt"}],
								queryParams: {
									limit: 20
								},
								filters: {
									entityType: {
										entities: supportedEntities.map((r) => {
											return {name: r.entity, filter: r.filter};
										})
									}
								}
							}),
							columns: [
								column({
									id: "entity",
									sortable: false,
									width: 48,
									htmlEncode: false,
									renderer: (columnValue, record, td, table1, storeIndex, column1) => {
										const cfg = entities.getLinkConfig(columnValue, record.filter);
										return `<div title="${cfg?.title.htmlEncode()}" class="icon ${cfg?.iconCls}"</div>`;
									}
								}),
								column({
									header: "Name",
									id: "name",
									sortable: true,
									resizable: true,
									htmlEncode: false,
									renderer: (columnValue, record) => {
										return `<h3>${record.name.htmlEncode()}</h3><h4>${record.description.htmlEncode()}</h4><h5>${Format.smartDateTime(record.modifiedAt)}</h5>`
									}
								})
							],
							listeners: {
								rowdblclick: () => {
									this.addToColumn();
								}
							}
						})
					)
				)
			),
			tbar({cls: "border-top"},
				"->",
				btn({
					cls: "filled primary",
					text: t("Add"),
					handler: () => {
						this.addToColumn();
					}
				})
			)
		);
	}

	private async addToColumn() {
		const selected = this.resultTable.rowSelection!.getSelected().map(row => row.record);

		const existingCards = await Promise.all(
			selected.map(s => cardDS.query({
				filter: {
					entity: s.entity,
					entityId: s.entityId,
					boardId: this.boardId
				}
			}))
		);

		await Promise.all(
			selected
				.filter((el, i) => !existingCards[i].ids.length)
				.map(s => cardDS.create({
					entity: s.entity,
					entityId: s.entityId,
					columnId: this.columnId,
					boardId: this.boardId
				}))
		);

		this.close();
	}
}